import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const FavoritesContext = createContext();

export const FavoritesProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [favoriteIds, setFavoriteIds] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchFavorites = useCallback(async () => {
    if (user && token) {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/favorites`);
        setFavorites(response.data.products || []);
        setFavoriteIds(response.data.product_ids || []);
      } catch (error) {
        console.error('Error fetching favorites:', error);
      } finally {
        setLoading(false);
      }
    } else {
      const savedFavorites = localStorage.getItem('vivid-favorites');
      if (savedFavorites) {
        const ids = JSON.parse(savedFavorites);
        setFavoriteIds(ids);
      }
    }
  }, [user, token]);

  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites]);

  useEffect(() => {
    if (!user) {
      localStorage.setItem('vivid-favorites', JSON.stringify(favoriteIds));
    }
  }, [favoriteIds, user]);

  const toggleFavorite = async (productId) => {
    if (user && token) {
      try {
        const response = await axios.post(`${API}/favorites/toggle`, { product_id: productId });
        await fetchFavorites();
        return response.data.is_favorite;
      } catch (error) {
        console.error('Error toggling favorite:', error);
        return false;
      }
    } else {
      const isFavorite = favoriteIds.includes(productId);
      if (isFavorite) {
        setFavoriteIds(prev => prev.filter(id => id !== productId));
      } else {
        setFavoriteIds(prev => [...prev, productId]);
      }
      return !isFavorite;
    }
  };

  const isFavorite = (productId) => {
    return favoriteIds.includes(productId);
  };

  return (
    <FavoritesContext.Provider value={{
      favorites,
      favoriteIds,
      loading,
      toggleFavorite,
      isFavorite,
      refreshFavorites: fetchFavorites
    }}>
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};
